import { CommentList } from "@/components";
import {
  DocumentTabHeader,
  MilestoneApproversCard,
  MilestoneNotificationCard,
  MilestonePanelDocuments,
  MilestonePanelCtqSnapshots,
} from "@/containers/Project/components";
import * as S from "@/containers/Project/components/styled";
import {
  CommentTarget,
  ProjectMilestoneStatus,
  ProjectUserPermissions,
} from "@/enums";
import { useMobileLayout } from "@/hooks/useAppMedia";
import { useProjectContext } from "@/providers";
import {
  useGetCommentsQuery,
  useGetDocumentsForMilestoneQuery,
} from "@/services";
import { faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons";
import { Badge, Col, Row, Tabs } from "antd";
import { bool, number, object } from "prop-types";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";

export const MilestonePanelExpanded = ({ milestone, skuRequired }) => {
  const { projectId } = useParams();
  const intl = useIntl();
  const { canUser, project } = useProjectContext();
  const isMobileLayout = useMobileLayout();

  const { data: projectDocuments, isLoading } =
    useGetDocumentsForMilestoneQuery(
      {
        projectId,
        milestoneId: milestone.id,
      },
      { skip: !milestone?.id || !projectId }
    );

  const [milestoneDocuments, setMilestoneDocuments] = useState([]);
  const [requiresDocumentsAlert, setRequiresDocumentsAlert] = useState(false);
  const [currentTab, setCurrentTab] = useState();

  const { data: { commentsCount: commentCount } = {} } = useGetCommentsQuery({
    projectId,
    target: CommentTarget.Milestone,
    targetId: milestone?.id,
  });

  useEffect(() => {
    const documents = projectDocuments?.[milestone.id];

    if (!documents) return;
    setMilestoneDocuments(documents);
  }, [milestone.id, projectDocuments]);

  useEffect(() => {
    if (!milestoneDocuments) return;

    setRequiresDocumentsAlert(
      !!milestone.documentsRequired && !milestoneDocuments.length
    );
  }, [milestone, milestoneDocuments]);

  useEffect(() => {
    if (
      milestone?.approvalRequired ||
      milestone?.status !== ProjectMilestoneStatus.Released
    ) {
      return;
    }

    setCurrentTab("documents");
  }, [milestone?.approvalRequired, milestone?.status]);

  const generateTabs = () => {
    const tabs = [];

    if (
      milestone?.approvalRequired ||
      milestone?.status !== ProjectMilestoneStatus.Released
    ) {
      tabs.push({
        key: "approvers",
        label: intl.formatMessage({
          id: milestone.approvalRequired
            ? "milestoneDetails.approvers"
            : "milestoneDetails.release",
        }),
        children: (
          <MilestoneApproversCard
            milestone={milestone}
            skuRequired={skuRequired}
          />
        ),
      });
    }

    tabs.push(
      {
        key: "documents",
        label: (
          <DocumentTabHeader
            documents={milestoneDocuments}
            requiresDocumentsAlert={requiresDocumentsAlert}
            isLoading={isLoading}
          />
        ),
        children: (
          <>
            <MilestonePanelDocuments milestone={milestone} />
            {requiresDocumentsAlert && (
              <S.CenteredIconContainer>
                <S.DocumentsRequiredAlert
                  type="error"
                  message={
                    <S.DocumentsRequiredAlertIcon
                      icon={faTriangleExclamation}
                    />
                  }
                  description={intl.formatMessage({
                    id: "project.documents.documentsRequiredAlert",
                  })}
                />
              </S.CenteredIconContainer>
            )}
          </>
        ),
      },
      {
        key: "notifications",
        label: intl.formatMessage({
          id: "milestoneDetails.notifications",
        }),
        children: <MilestoneNotificationCard milestone={milestone} />,
      },
      {
        key: "comments",
        label: (
          <>
            {intl.formatMessage({
              id: "milestoneDetails.comments",
            })}{" "}
            <Badge count={commentCount} showZero />
          </>
        ),
        children: (
          <CommentList
            projectId={projectId}
            target={CommentTarget.Milestone}
            targetId={milestone.id}
            showTitle={false}
            editable={canUser(
              ProjectUserPermissions.EditMilestoneComments,
              milestone
            )}
            totalCommentsCount={commentCount}
          />
        ),
     }
   );

   if (!project.isCtqDisabled) {
    tabs.push(
     {
       key: "ctqSnapshots",
       label: intl.formatMessage({
        id: "milestoneDetails.ctqSnapshots",
       }),
       children: <MilestonePanelCtqSnapshots milestone={milestone} />
     }
    );
   }

    return tabs;
  };

  const handleTabChange = ({ key }) => {
    setCurrentTab(key);
  };

  return (
    <>
      <Row justify="center">
        <Col flex={1}>
          <Tabs
            data-test-id="milestone-details-tabs"
            activeKey={currentTab}
            defaultActiveKey={"approvers"}
            onChange={handleTabChange}
            size={"medium"}
            centered={!isMobileLayout}
            items={generateTabs()}
          />
        </Col>
      </Row>
    </>
  );
};

MilestonePanelExpanded.propTypes = {
  milestone: object,
  childLevel: number,
  skuRequired: bool,
};
